// The people on a customer's account.
//
// How many they get is a service-package term (the "Account users" tier
// feature), so the allowance is shown next to the list — an operator who can't
// add someone needs to see "3 of 3 on Business Standard" rather than discover it
// by getting refused.
//
// Two independent switches per person: a portal login, and voicemail
// notifications. An office manager may want voicemail email without a login; a
// bookkeeper the reverse.

import { useState } from 'react';
import ActionMenu from '../shared/ActionMenu.jsx';
import AddButton from '../shared/AddButton.jsx';
import FormModal from '../shared/FormModal.jsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <label className={`inline-flex items-center gap-1.5 text-xs ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input type="checkbox" checked={checked} disabled={disabled}
             onChange={(e) => onChange(e.target.checked)}
             className="rounded border-gray-300" />
      <span className="text-gray-700">{label}</span>
    </label>
  );
}

function AllowanceBadge({ allowance }) {
  if (!allowance) return null;
  const { limit, used, tier } = allowance;
  if (limit == null) {
    return (
      <span className="text-xs text-gray-500" title="No service package assigned, so no limit is enforced.">
        {used} user{used === 1 ? '' : 's'} · no package limit
      </span>
    );
  }
  const full = used >= limit;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
      full ? 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200' : 'bg-gray-100 text-gray-700'
    }`}>
      {used} of {limit} user{limit === 1 ? '' : 's'}{tier ? ` · ${tier}` : ''}
    </span>
  );
}

export default function AccountUsersPanel({ subscriberId, canOperate }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', portal_access: true, voicemail_notify: true, sms_notify: true });
  const [err, setErr] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['subscriber', String(subscriberId), 'users'],
    queryFn: () => api.get(`/subscribers/${subscriberId}/users`),
  });

  const users = data?.users || [];
  const allowance = data?.allowance || null;
  const atLimit = allowance?.limit != null && allowance.used >= allowance.limit;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['subscriber', String(subscriberId)] });
  const onError = (e) => setErr(e?.body?.error || e?.message || 'That didn’t work.');

  const addUser = useMutation({
    mutationFn: () => api.post(`/subscribers/${subscriberId}/users`, form),
    onSuccess: () => {
      setErr(null); setAdding(false);
      setForm({ name: '', email: '', portal_access: true, voicemail_notify: true, sms_notify: true });
      invalidate();
    },
    onError,
  });

  const patchUser = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/subscribers/${subscriberId}/users/${id}`, body),
    onSuccess: () => { setErr(null); invalidate(); },
    onError,
  });

  const removeUser = useMutation({
    mutationFn: (id) => api.delete(`/subscribers/${subscriberId}/users/${id}`),
    onSuccess: () => { setErr(null); setConfirmRemove(null); invalidate(); },
    onError,
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Account users</h2>
          <p className="mt-1 text-sm text-gray-500">
            People on this account. Each can sign into the portal and receive voicemail by email.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AllowanceBadge allowance={allowance} />
          {/* HIDDEN WHILE THE MODAL IS OPEN. FormModal does not trap focus yet, so a trigger
              left rendered behind the backdrop is still tab-reachable — and it carries the
              same accessible name as the modal's submit, so "Add user" would mean two things
              at once. */}
          {canOperate && !adding && (
            <AddButton
              onClick={() => { setAdding(true); setErr(null); }}
              disabled={atLimit}
              title={atLimit
                ? `The ${allowance?.tier || 'current'} package includes ${allowance?.limit} user${allowance?.limit === 1 ? '' : 's'}. Remove one, or move the account to a larger package.`
                : undefined}
            >
              Add user
            </AddButton>
          )}
        </div>
      </div>

      {atLimit && canOperate && (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This account is using all {allowance.limit} user{allowance.limit === 1 ? '' : 's'} included in
          {allowance.tier ? ` its ${allowance.tier} package` : ' its package'}. Remove someone, or change the
          package on the account&rsquo;s Service tier to add more.
        </p>
      )}

      {/* A MODAL, NOT AN EXPANDING PANEL. This pushed the roster down the page and put the
          Save button wherever the list happened to end — see components/shared/FormModal.jsx
          for why all four tabs now agree. */}
      {adding && (
        <FormModal
          title="Add user"
          subtitle="They can sign into the portal and receive voicemail by email."
          onClose={() => { setAdding(false); setErr(null); addUser.reset(); }}
          footer={(
            <>
              <button onClick={() => addUser.mutate()} disabled={!form.email.trim() || addUser.isPending}
                      className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {addUser.isPending ? 'Adding…' : 'Add user'}
              </button>
              <button onClick={() => { setAdding(false); setErr(null); addUser.reset(); }}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              {err && <span className="text-xs text-red-700">{err}</span>}
            </>
          )}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Name</span>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     placeholder="Michael Smith"
                     className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Email</span>
              <input value={form.email} type="email" autoFocus
                     onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                     placeholder="michael@example.com"
                     className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <Toggle label="Portal login" checked={form.portal_access}
                    onChange={(v) => setForm((f) => ({ ...f, portal_access: v }))} />
            <Toggle label="Voicemail by email" checked={form.voicemail_notify}
                    onChange={(v) => setForm((f) => ({ ...f, voicemail_notify: v }))} />
            <Toggle label="Text messages by email" checked={form.sms_notify}
                    onChange={(v) => setForm((f) => ({ ...f, sms_notify: v }))} />
          </div>
        </FormModal>
      )}

      {/* NOT WHILE THE MODAL IS OPEN — it shows the failure in its own footer, next to the
          button that caused it. `err` is shared with the row actions (remove, toggle), which
          is why it still lives out here the rest of the time. */}
      {err && !adding && (
        <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</p>
      )}

      {isLoading ? (
        <p className="mt-4 text-sm text-gray-500">Loading…</p>
      ) : users.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">
          Nobody on this account yet. Add a user so they can receive voicemail and sign into the portal.
        </p>
      ) : (
        <div className="mt-4 overflow-visible rounded-md border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Portal login</th>
                <th className="px-3 py-2">Voicemail email</th>
                <th className="px-3 py-2">Text email</th>
                {canOperate && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => {
                const disabled = u.status !== 'active';
                return (
                  <tr key={u.id} className={disabled ? 'bg-gray-50/60 text-gray-400' : ''}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-gray-900">{u.name || <span className="text-gray-400">—</span>}</span>
                      {u.isPrimary && (
                        <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600"
                              title="The account's main contact.">
                          main contact
                        </span>
                      )}
                      {disabled && (
                        <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                          switched off
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{u.email}</td>
                    <td className="px-3 py-2">
                      <Toggle label={u.portalAccess ? 'Yes' : 'No'} checked={u.portalAccess} disabled={!canOperate || disabled}
                              onChange={(v) => patchUser.mutate({ id: u.id, body: { portal_access: v } })} />
                    </td>
                    <td className="px-3 py-2">
                      <Toggle label={u.voicemailNotify ? 'Yes' : 'No'} checked={u.voicemailNotify} disabled={!canOperate || disabled}
                              onChange={(v) => patchUser.mutate({ id: u.id, body: { voicemail_notify: v } })} />
                    </td>
                    <td className="px-3 py-2">
                      <Toggle label={u.smsNotify ? 'Yes' : 'No'} checked={u.smsNotify} disabled={!canOperate || disabled}
                              onChange={(v) => patchUser.mutate({ id: u.id, body: { sms_notify: v } })} />
                    </td>
                    {canOperate && (
                      <td className="px-3 py-2 text-right">
                        {/* One kebab per row, like every other table on the site.
                            The two toggles stay inline because they're STATE an
                            operator reads at a glance, not actions. */}
                        <ActionMenu
                          title={`Actions for ${u.name || u.email}`}
                          items={[
                            { label: 'Edit name or email', onClick: () => { setEditing(u); setEditForm({ name: u.name || '', email: u.email }); setErr(null); } },
                            ...(!u.isPrimary ? [{
                              label: 'Make main contact',
                              disabled,
                              title: disabled ? 'Switch them back on first' : 'Voicemail and account mail default to the main contact',
                              onClick: () => patchUser.mutate({ id: u.id, body: { make_primary: true } }),
                            }] : []),
                            { divider: true },
                            ...(disabled ? [{
                              label: 'Switch back on',
                              onClick: () => patchUser.mutate({ id: u.id, body: { status: 'active' } }),
                            }] : [{
                              label: 'Switch off (keep on the account)',
                              disabled: u.isPrimary,
                              title: u.isPrimary ? 'Hand the main contact over first' : 'Keeps the record; stops their login and voicemail email',
                              onClick: () => patchUser.mutate({ id: u.id, body: { status: 'disabled' } }),
                            }]),
                            {
                              label: 'Remove from account',
                              danger: true,
                              disabled: u.isPrimary,
                              title: u.isPrimary ? 'Hand the main contact over first' : undefined,
                              onClick: () => setConfirmRemove(u),
                            },
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
              {editing && (
                <tr className="bg-blue-50/40">
                  <td colSpan={canOperate ? 6 : 5} className="px-3 py-3">
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="block">
                        <span className="block text-xs font-medium text-gray-700">Name</span>
                        <input
                          value={editForm.name}
                          onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                          className="mt-1 w-44 rounded-md border border-gray-300 px-2 py-1 text-sm"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-xs font-medium text-gray-700">Email</span>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                          className="mt-1 w-64 rounded-md border border-gray-300 px-2 py-1 text-sm"
                        />
                      </label>
                      <button
                        onClick={() => patchUser.mutate({
                          id: editing.id,
                          body: { name: editForm.name.trim(), email: editForm.email.trim() },
                        }, { onSuccess: () => setEditing(null) })}
                        disabled={!editForm.email.trim() || patchUser.isPending}
                        className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {patchUser.isPending ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={() => { setEditing(null); setErr(null); }}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                    {/* The email IS the portal login, so say so rather than
                        letting someone change it and wonder why the customer
                        can't sign in with the old one. */}
                    <p className="mt-2 text-xs text-gray-600">
                      This email is both where voicemail is sent and the address they sign in with —
                      changing it changes their login.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {confirmRemove && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-800">
            Remove <span className="font-medium">{confirmRemove.name || confirmRemove.email}</span> from this account?
            They&rsquo;ll stop receiving voicemail{confirmRemove.portalAccess ? ' and lose their portal login' : ''}.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button onClick={() => removeUser.mutate(confirmRemove.id)} disabled={removeUser.isPending}
                    className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
              {removeUser.isPending ? 'Removing…' : 'Remove user'}
            </button>
            <button onClick={() => { setConfirmRemove(null); removeUser.reset(); setErr(null); }}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
